/** Client-only Ritual records. The legacy key is retained for safe v1 compatibility. */
export type RitualTargetMode = 'weight' | 'dose';
export type RitualOrigin = 'manual' | 'dose-calculator' | 'blend-calculator';

export interface RitualAllocation {
  productId: string | null;
  brand: string;
  productName: string;
  thcPercent: number | null;
  cbdPercent: number | null;
  weightGrams: number;
  thcDoseMg: number;
  cbdDoseMg: number;
}

export interface JournalEntry {
  id: string;
  createdAt: string;
  updatedAt: string;
  occurredAt: string;
  title: string;
  notes: string;
  /** Preserved from legacy entries and used as their consumption summary. */
  blendLabel: string;
  mood: string;
  effects: string;
  favourite: boolean;
  allocations: RitualAllocation[];
  targetMode: RitualTargetMode;
  origin: RitualOrigin;
}

export type RitualEntry = JournalEntry;
export const JOURNAL_STORAGE_KEY = 'flower-blend.journal.v1';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function finiteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

export function normalizeRitualAllocation(value: unknown): RitualAllocation | null {
  if (!isRecord(value)) return null;
  if (!(
    (value.productId === null || typeof value.productId === 'string') &&
    typeof value.brand === 'string' &&
    typeof value.productName === 'string' &&
    (value.thcPercent === null ||
      (typeof value.thcPercent === 'number' && Number.isFinite(value.thcPercent))) &&
    finiteNonNegative(value.weightGrams) &&
    finiteNonNegative(value.thcDoseMg)
  )) return null;
  const cbdPercent = value.cbdPercent === null || value.cbdPercent === undefined
    ? null
    : typeof value.cbdPercent === 'number' && Number.isFinite(value.cbdPercent)
      ? value.cbdPercent
      : undefined;
  if (cbdPercent === undefined || (value.cbdDoseMg !== undefined && !finiteNonNegative(value.cbdDoseMg))) return null;
  return {
    productId: value.productId,
    brand: value.brand,
    productName: value.productName,
    thcPercent: value.thcPercent,
    cbdPercent,
    weightGrams: value.weightGrams,
    thcDoseMg: value.thcDoseMg,
    cbdDoseMg: finiteNonNegative(value.cbdDoseMg)
      ? value.cbdDoseMg
      : value.weightGrams * (cbdPercent ?? 0) * 10,
  };
}

export function isRitualAllocation(value: unknown): value is RitualAllocation {
  return normalizeRitualAllocation(value) !== null;
}

/** Accepts both original Journal records and expanded Ritual records. */
export function isJournalEntry(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === 'string' &&
    typeof value.createdAt === 'string' &&
    typeof value.title === 'string' &&
    typeof value.notes === 'string'
  );
}

export function normalizeJournalEntry(value: unknown): JournalEntry | null {
  if (!isJournalEntry(value) || !isRecord(value)) return null;
  const allocations = Array.isArray(value.allocations)
    ? value.allocations.map(normalizeRitualAllocation).filter((allocation): allocation is RitualAllocation => allocation !== null)
    : [];
  return {
    id: value.id as string,
    createdAt: value.createdAt as string,
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : value.createdAt as string,
    occurredAt: typeof value.occurredAt === 'string' ? value.occurredAt : value.createdAt as string,
    title: value.title as string,
    notes: value.notes as string,
    blendLabel: typeof value.blendLabel === 'string' ? value.blendLabel : '',
    mood: typeof value.mood === 'string' ? value.mood : '',
    effects: typeof value.effects === 'string' ? value.effects : '',
    favourite: typeof value.favourite === 'boolean' ? value.favourite : false,
    allocations,
    targetMode: value.targetMode === 'dose' ? 'dose' : 'weight',
    origin: value.origin === 'dose-calculator' || value.origin === 'blend-calculator'
      ? value.origin
      : 'manual',
  };
}

export function readJournalEntries(raw: string | null): JournalEntry[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeJournalEntry).filter((entry): entry is JournalEntry => entry !== null);
  } catch {
    return [];
  }
}

function readRaw(): JournalEntry[] {
  return readJournalEntries(localStorage.getItem(JOURNAL_STORAGE_KEY));
}

function writeAll(entries: JournalEntry[]): void {
  localStorage.setItem(JOURNAL_STORAGE_KEY, JSON.stringify(entries));
}

export const journalStore = {
  list(): JournalEntry[] {
    return readRaw().sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
  },
  create(input: Omit<JournalEntry, 'id' | 'createdAt' | 'updatedAt'>): JournalEntry {
    const now = new Date().toISOString();
    const entry = { ...input, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
    writeAll([entry, ...readRaw()]);
    return entry;
  },
  update(id: string, patch: Partial<Omit<JournalEntry, 'id' | 'createdAt'>>): JournalEntry | null {
    const all = readRaw();
    const index = all.findIndex((entry) => entry.id === id);
    if (index < 0) return null;
    const updated = { ...all[index]!, ...patch, createdAt: all[index]!.createdAt, updatedAt: new Date().toISOString() };
    all[index] = updated;
    writeAll(all);
    return updated;
  },
  remove(id: string): boolean {
    const all = readRaw();
    const next = all.filter((entry) => entry.id !== id);
    if (next.length === all.length) return false;
    writeAll(next);
    return true;
  },
};
